/**
 * parseIntent — parsing déterministe des INTENTIONS vocales (US-VDPI-09 AC1).
 *
 * Orthogonal au NUMÉRO (parseNumber = sélection d'instance) et aux mots de
 * CONTRÔLE (parseControl). Vocabulaire FIXE prononçable (audit §10.2). Pur,
 * sans dépendance DOM : testable en isolation.
 */

import { parseSpokenInt } from './parseNumber';

export type IntentName =
  | 'prescriptions'
  | 'administrer'
  | 'administrer_autre'
  | 'constantes'
  | 'transmission'
  | 'score'
  | 'refus'
  | 'surveillance'
  | 'relire';

export type IntentResult = { intent: IntentName; numero?: number } | { intent: 'none' };

// Synonymes normalisés (minuscules, sans accents) — robustesse ASR FR.
const ADMIN_WORDS = ['administrer', 'administre'];
const PRESCRIPTIONS_WORDS = ['prescriptions', 'prescription', 'medicaments', 'medicament', 'traitements', 'traitement'];
const CONSTANTES_WORDS = ['constantes', 'constante'];
const TRANSMISSION_WORDS = ['transmission', 'transmissions', 'transmettre'];
const SCORE_WORDS = ['score', 'algoplus'];
const REFUS_WORDS = ['refus', 'refuse', 'refuser'];
const SURVEILLANCE_WORDS = ['surveillance', 'surveiller'];
const RELIRE_WORDS = ['relire', 'relis', 'repete', 'repeter', 'relecture'];

/** Normalise : minuscules, sans accents, ponctuation → espaces (identique parseControl). */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function parseIntent(text: string): IntentResult {
  const norm = normalize(text);
  if (!norm) return { intent: 'none' };
  const tokens = norm.split(' ');
  const has = (words: string[]) => tokens.some((t) => words.includes(t));

  // Priorité (§10.2) : administrer + nombre > administrer_autre > intentions simples.
  if (has(ADMIN_WORDS)) {
    const numero = parseSpokenInt(norm);
    if (numero !== null) return { intent: 'administrer', numero };
    // "administrer" / "administrer autre" sans numéro → capture guidée "autre" (câblage V1).
    return { intent: 'administrer_autre' };
  }
  if (tokens.includes('autre')) return { intent: 'administrer_autre' }; // "autre medicament"
  if (has(PRESCRIPTIONS_WORDS)) return { intent: 'prescriptions' };
  if (has(CONSTANTES_WORDS)) return { intent: 'constantes' };
  if (has(TRANSMISSION_WORDS)) return { intent: 'transmission' };
  if (has(SCORE_WORDS)) return { intent: 'score' };
  if (has(REFUS_WORDS)) return { intent: 'refus' };
  if (has(SURVEILLANCE_WORDS)) return { intent: 'surveillance' };
  if (has(RELIRE_WORDS)) return { intent: 'relire' };
  return { intent: 'none' };
}
